import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCart } from '../../contexts/CartContext';

const StickyMobileCart = () => {
  const { cart, getItemCount, getTotal, toggleCart } = useCart();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const itemCount = getItemCount();
  const total = getTotal();

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlNavbar);
    return () => window.removeEventListener('scroll', controlNavbar);
  }, [lastScrollY]);

  // Auto-hide preview after 3 seconds
  useEffect(() => {
    if (showPreview) {
      const timer = setTimeout(() => setShowPreview(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showPreview]);

  // Only show on mobile and when cart has items
  if (itemCount === 0) return null;

  return (
    <>
      {/* Cart preview drawer */}
      {showPreview && (
        <div className="md:hidden fixed bottom-20 left-0 right-0 mx-4 z-40 bg-white rounded-t-2xl shadow-2xl border-t border-gray-200 transform transition-transform duration-300">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">העגלה שלך</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {cart.items?.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                  <img
                    src={item.product?.images?.[0] || '/placeholder-product.jpg'}
                    alt={item.product?.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.product?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.quantity}x ₪{item.product?.price_b2c}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {cart.items?.length > 3 && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                +{cart.items.length - 3} מוצרים נוספים
              </p>
            )}
            
            <Link
              to="/cart"
              className="mt-3 block w-full bg-primary-600 text-white text-center py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              onClick={() => setShowPreview(false)}
            >
              צפה בעגלה המלאה
            </Link>
          </div>
        </div>
      )}

      {/* Sticky cart bar */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Cart info */}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-3 flex-1"
              >
                <div className="relative">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <ShoppingBagIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                </div>
                
                <div className="text-right flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {itemCount} {itemCount === 1 ? 'מוצר' : 'מוצרים'}
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    ₪{total.toFixed(2)}
                  </div>
                </div>
              </button>

              {/* Checkout button */}
              <Link
                to="/checkout"
                className="bg-gradient-to-r from-primary-600 to-pink-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
              >
                לקופה
              </Link>
            </div>
          </div>
          
          {/* Progress bar for free shipping */}
          {total < 200 && (
            <div className="px-4 pb-3">
              <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-600 h-full transition-all duration-500"
                  style={{ width: `${Math.min((total / 200) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1 text-center">
                עוד ₪{(200 - total).toFixed(2)} למשלוח חינם! 🚚
              </p>
            </div>
          )}
          
          {total >= 200 && (
            <div className="px-4 pb-3">
              <p className="text-xs text-green-600 font-semibold text-center flex items-center justify-center gap-1">
                <span>✓</span>
                <span>זכאי למשלוח חינם!</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StickyMobileCart;

